/**
 * Predictive Search — Crochet Co Theme
 * Implements the Shopify Section Rendering API to provide live drop-down
 * search results from the header input.
 *
 * Requirements:
 *  - Wrap search <form> with <predictive-search> in header.liquid
 *  - sections/predictive-search.liquid must exist (already present)
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[name="q"]');
    this.resultsContainer = null;
    this.debounceTimer = null;
    this.currentQuery = '';
    this.abortController = null;
  }

  connectedCallback() {
    if (!this.input) return;

    // Create results container
    this.resultsContainer = document.createElement('div');
    this.resultsContainer.id = 'PredictiveSearchResults';
    this.resultsContainer.setAttribute('role', 'listbox');
    this.resultsContainer.setAttribute('aria-label', 'Search results');
    this.appendChild(this.resultsContainer);

    // Listen for input changes
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', () => {
      if (this.input.value.length > 1) this.fetchResults(this.input.value);
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  onInput() {
    const query = this.input.value.trim();
    clearTimeout(this.debounceTimer);

    if (query.length < 2) {
      this.close();
      return;
    }

    if (query === this.currentQuery) return;

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 250);
  }

  fetchResults(query) {
    this.currentQuery = query;

    // Cancel any in-flight request
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query,collection,article',
      'resources[limit]': '5',
      section_id: 'predictive-search',
    });

    this.resultsContainer.setAttribute('aria-busy', 'true');

    fetch(`${window.Shopify.routes.root}search/suggest?${params}`, {
      signal: this.abortController.signal,
    })
      .then((r) => r.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = doc.getElementById('predictive-search-results');

        if (results) {
          this.resultsContainer.innerHTML = results.innerHTML;
          this.resultsContainer.setAttribute('aria-busy', 'false');
          this.resultsContainer.classList.add('is-open');
          this.input.setAttribute('aria-expanded', 'true');
        } else {
          this.close();
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.warn('[PredictiveSearch] Fetch error:', err);
          this.close();
        }
      });
  }

  close() {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.currentQuery = '';
  }
}

customElements.define('predictive-search', PredictiveSearch);
